// The "Send to Shannon" card on the Commission board (Oct 28, 2026): what commission_payout_report_status()
// answers, and the words the card shows. Greg: "Just 1 with that weeks commissions on it for all ... a button for
// Chad to click when done and send to Shannon". The email itself is supabase/functions/commission-payout-report
// (its render.ts supplies the day spans and money here, so the card and the email say the same thing).

import { daySpan, etDay, fmtUSD } from "../../supabase/functions/commission-payout-report/render";

export interface ReportRecipient { email: string; name: string | null }

export type ReportState = "sending" | "sent" | "failed" | "skipped";

export interface LastReport {
  id: string;
  status: ReportState;
  created_at: string;
  sent_at: string | null;
  sent_by: string | null;
  total: number;
  bills: number;
  people: number | null;
  attempts: number;
  error_detail: string | null;
  skip_reason: string | null;
}

export interface ReportStatus {
  mode: "on" | "off";
  can_send: boolean;
  can_resend: boolean;
  pending: { bills: number; total: number; people: number; held: number; first_approved_at: string | null; last_approved_at: string | null };
  last: LastReport | null;
  to: ReportRecipient[];
  cc: ReportRecipient[];
}

const num = (v: unknown): number => (Number.isFinite(Number(v)) ? Number(v) : 0);
const str = (v: unknown): string | null => (typeof v === "string" && v.trim() !== "" ? v : null);
const STATES: ReportState[] = ["sending", "sent", "failed", "skipped"];

function recipients(v: unknown): ReportRecipient[] {
  return (Array.isArray(v) ? v : [])
    .map((r) => ({ email: str((r as Record<string, unknown>)?.email) ?? "", name: str((r as Record<string, unknown>)?.name) }))
    .filter((r) => r.email);
}

/** commission_payout_report_status()'s JSON → the card's state (null when it is not an object). */
export function normalizeStatus(raw: unknown): ReportStatus | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const p = (r.pending ?? {}) as Record<string, unknown>;
  const l = r.last as Record<string, unknown> | null | undefined;
  const lastState = str(l?.status) as ReportState | null;
  return {
    mode: str(r.mode)?.toLowerCase() === "off" ? "off" : "on",
    can_send: r.can_send === true,
    can_resend: r.can_resend === true,
    pending: {
      bills: num(p.bills), total: num(p.total), people: num(p.people), held: num(p.held),
      first_approved_at: str(p.first_approved_at), last_approved_at: str(p.last_approved_at),
    },
    last: l && str(l.id) && lastState && STATES.includes(lastState)
      ? {
          id: String(l.id), status: lastState, created_at: str(l.created_at) ?? "", sent_at: str(l.sent_at),
          sent_by: str(l.sent_by), total: num(l.total), bills: num(l.bills), people: l.people == null ? null : num(l.people),
          attempts: num(l.attempts), error_detail: str(l.error_detail), skip_reason: str(l.skip_reason),
        }
      : null,
    to: recipients(r.to),
    cc: recipients(r.cc),
  };
}

const plural = (n: number, one: string, many = one + "s") => `${n} ${n === 1 ? one : many}`;
const firstName = (p: ReportRecipient) => (p.name ? p.name.trim().split(/\s+/)[0] : p.email);
const fullName = (p: ReportRecipient) => p.name?.trim() || p.email;

/** "Shannon Ser", "Shannon Ser and Greg Bardin", "A, B and C". */
export function namesOf(people: ReportRecipient[]): string {
  const names = people.map(fullName);
  return names.length <= 1 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** The button: "Send to Shannon" (the first recipient's first name). */
export function sendLabel(s: ReportStatus): string {
  return s.to.length ? `Send to ${firstName(s.to[0])}` : "Send the report";
}

/** "Oct 28, 10:42 AM" in Eastern time. */
export function etStamp(ts: string): string {
  return new Date(ts).toLocaleString("en-US", {
    timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  }).replace(/\s+/g, " ");
}

/** What is waiting: "17 approved payouts · $22,207.71 to 6 people · approved Oct 28 · not sent yet". */
export function pendingLine(s: ReportStatus): string {
  const p = s.pending;
  if (p.bills === 0) return "Everything approved is on a report already.";
  const span = p.first_approved_at && p.last_approved_at ? daySpan(etDay(p.first_approved_at), etDay(p.last_approved_at)) : "";
  return [
    plural(p.bills, "approved payout"),
    `${fmtUSD(p.total)} to ${plural(p.people, "person", "people")}`,
    span ? `approved ${span.replace(/, \d{4}$/, "")}` : "",
    p.held ? `${p.held} held` : "",
    "not sent yet",
  ].filter(Boolean).join(" · ");
}

/** The question before sending. */
export function confirmText(s: ReportStatus): string {
  const p = s.pending;
  const to = s.to.length ? namesOf(s.to) : "the report's recipients";
  const cc = s.cc.length ? ` ${namesOf(s.cc)} ${s.cc.length === 1 ? "gets" : "get"} a copy.` : "";
  return `Email ${to} one report of these ${plural(p.bills, "payout")} (${fmtUSD(p.total)}, ${plural(p.people, "person", "people")})?${cc} `
    + "Anything approved after this goes on the next report.";
}

/** The last report, in a line: sent / on its way / not sent and why. */
export function lastLine(s: ReportStatus): { text: string; tone: "ok" | "busy" | "bad" | "muted" } | null {
  const l = s.last;
  if (!l) return null;
  const what = `${fmtUSD(l.total)}${l.people ? ` to ${plural(l.people, "person", "people")}` : `, ${plural(l.bills, "payout")}`}`;
  const by = l.sent_by ? ` by ${l.sent_by}` : "";
  if (l.status === "sent") return { text: `Last report: sent ${etStamp(l.sent_at ?? l.created_at)}${by} — ${what}`, tone: "ok" };
  if (l.status === "sending") return { text: `Sending the report${by} — ${what}…`, tone: "busy" };
  if (l.status === "failed") {
    const retry = l.attempts < 3 ? " It tries again within the hour." : "";
    return { text: `The last report (${what}) didn't send: ${l.error_detail ?? "unknown error"}.${retry}`, tone: "bad" };
  }
  return { text: `The last report (${what}) was not sent: ${l.skip_reason ?? "skipped"}.`, tone: "muted" };
}
